// URLs firmadas de Storage (duran 1 h) — lógica PURA (firmas.test.ts). Las vistas
// retienen los casos más de una hora (Casos carga el dataset una vez; Comparar cachea los
// detalles; Corridas conserva `casos` entre polls), y pasada la hora el `<img>` falla en
// silencio. Acá se lee CUÁNDO vencen para renovarlas antes (useRefrescoFirmas) y para que
// el Lightbox diga la causa en vez de quedarse en negro.

import type { Caso } from './tipos';

/** Con este margen antes del `exp` se considera "por vencer": el intervalo del hook es de 1 min y el navegador en segundo plano lo estira. */
export const MARGEN_VENCIMIENTO_MS = 5 * 60_000;

function decodificarBase64Url(s: string): string | null {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const resto = b64.length % 4;
  const pad = resto === 0 ? '' : '='.repeat(4 - resto);
  try {
    return atob(b64 + pad);
  } catch {
    return null;
  }
}

/**
 * Vencimiento (ms epoch) de una URL firmada de Supabase Storage: el query param `token`
 * es un JWT cuyo payload trae `exp` en segundos. null si no hay token, no parsea o no
 * trae `exp` numérico (una URL pública no vence).
 */
export function expiracionDeUrlFirmada(url: string | null | undefined): number | null {
  if (!url) return null;
  const m = /[?&]token=([^&#]+)/.exec(url);
  if (!m) return null;
  let token: string;
  try {
    token = decodeURIComponent(m[1]);
  } catch {
    return null;
  }
  const partes = token.split('.');
  if (partes.length < 2) return null;
  const json = decodificarBase64Url(partes[1]);
  if (json === null) return null;
  try {
    const payload: unknown = JSON.parse(json);
    if (typeof payload !== 'object' || payload === null) return null;
    const exp = (payload as { exp?: unknown }).exp;
    return typeof exp === 'number' && Number.isFinite(exp) ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/** ¿Un vencimiento (ms) cae dentro del margen? Lo ya vencido también cuenta. */
export function porVencer(expMs: number, ahora: number = Date.now(), margenMs: number = MARGEN_VENCIMIENTO_MS): boolean {
  return expMs - ahora <= margenMs;
}

/** Una URL sin token (o ilegible) nunca está "por vencer": no hay nada que renovar. */
export function urlPorVencer(url: string | null | undefined, ahora: number = Date.now(), margenMs: number = MARGEN_VENCIMIENTO_MS): boolean {
  const exp = expiracionDeUrlFirmada(url);
  return exp !== null && porVencer(exp, ahora, margenMs);
}

/**
 * Vencimiento de una tanda de casos: el del PRIMER caso con URL de generada (todas las
 * URLs de una misma respuesta se firman juntas, así que una alcanza). null sin URLs.
 */
export function vencimientoDeCasos(casos: Caso[]): number | null {
  for (const c of casos) {
    const exp = expiracionDeUrlFirmada(c.generacion.url) ?? expiracionDeUrlFirmada(c.generacion.url_full);
    if (exp !== null) return exp;
  }
  return null;
}

export function casosPorVencer(casos: Caso[], ahora: number = Date.now(), margenMs: number = MARGEN_VENCIMIENTO_MS): boolean {
  const exp = vencimientoDeCasos(casos);
  return exp !== null && porVencer(exp, ahora, margenMs);
}

/**
 * De varias tandas firmadas en momentos distintos (Comparar: cada corrida se pidió
 * cuando se la agregó), la que vence PRIMERO: es la que tiene que disparar la renovación
 * de todas. Sin ninguna con vencimiento, la primera no vacía (o []).
 */
export function casosQueVencenPrimero(grupos: Caso[][]): Caso[] {
  let mejor: Caso[] | null = null;
  let mejorExp = Number.POSITIVE_INFINITY;
  for (const g of grupos) {
    const exp = vencimientoDeCasos(g);
    if (exp !== null && exp < mejorExp) {
      mejorExp = exp;
      mejor = g;
    }
  }
  return mejor ?? grupos.find((g) => g.length > 0) ?? [];
}

/**
 * Los casos locales reemplazados por los frescos (URLs nuevas), POR ID y en el orden
 * local: la selección y el formulario viven aparte y no se tocan. Si uno no volvió se
 * conserva el local; si el fresco no trae motivo, se conserva el que ya estaba.
 */
export function refrescarCasos(locales: Caso[], frescos: Caso[]): Caso[] {
  const porId = new Map(frescos.map((c) => [c.imagen_id, c]));
  return locales.map((local) => {
    const fresco = porId.get(local.imagen_id);
    if (!fresco) return local;
    return { ...fresco, motivo_edicion: fresco.motivo_edicion ?? local.motivo_edicion };
  });
}
